import { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Accordion from "@mui/material/Accordion";
import { StyledAccordionSummary } from "../../styles/componentStyled";
import AccordionDetails from "@mui/material/AccordionDetails";
import Grid from "@mui/material/Grid";
import { Button, Divider, Stack, Typography } from "@mui/material";
import { Assignment, ExpandMore } from "@mui/icons-material";
import Toast from "../../components/Layout/Toast";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  createEmployee,
  resetSelectedVisa,
  selectProcesses,
  setSelectedEmployee,
} from "./processSlices";

import {
  Employee,
  EmployeeStatus,
  MedicalStatus,
  NotificationMessageTypes,
} from "./types/processTypes";
import { isNoEmbassyCountry } from "./processConstants";
import { FormikTextField } from "../../components/Layout/FormikTextField";
import { EmployeeRemarksButton } from "./components/EmployeeRemarksButton";
import { Form, Formik, FormikProps } from "formik";
import { createVisaSchema } from "./validation";
import { useTranslation } from "react-i18next";
import { Company } from "../setups/types/setupTypes";
import { selectAuth } from "../auth/authSlice";
import { ClientTypes } from "../auth/types/authType";
import { CompanyFilter } from "../../components/filter/CompanyFilter";
import {
  applyOfficeSelectionToEmployee,
  officePartnerSourceFromContext,
  resolveDefaultSelectOffice,
  resolveForeignOfficePartner,
} from "./utils/officePartners";
import type { OfficePartnerContext } from "../calling/types/officePartnerContext";

interface CandidateId {
  currentEmployee?: Employee;
  handleDialogClose: Function;
  officeContext?: OfficePartnerContext | null;
}

export const VisaEntry = ({
  currentEmployee,
  handleDialogClose,
  officeContext,
}: CandidateId) => {
  const { t } = useTranslation();

  const [company, setCompany] = useState<Company>();

  const {
    selectedEmployee,
    error: visaFailed,
    success: visaAdded,
  } = useAppSelector(selectProcesses);

  const dispatch = useAppDispatch();
  const { user } = useAppSelector(selectAuth);

  const partnerSource = useMemo(
    () => officePartnerSourceFromContext(officeContext, user ?? undefined),
    [officeContext, user],
  );

  useEffect(() => {
    if (
      currentEmployee &&
      currentEmployee._id &&
      currentEmployee._id.length > 10
    ) {
      dispatch(
        setSelectedEmployee({
          ...currentEmployee,
          visa: {
            visaNumber: "",
            passportNumber: "",
            fullName: "",
            ...(currentEmployee.visa ?? {}),
          },
        }),
      );
    } else {
      resetFields();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, currentEmployee]);

  useEffect(() => {
    if (visaAdded) {
      handleDialogClose();
    }
  }, [visaAdded]);

  function resetFields() {
    dispatch(resetSelectedVisa());
  }

  const employeeForOffice = selectedEmployee ?? currentEmployee;

  const foreignOfficePartner = useMemo(
    () => resolveForeignOfficePartner(employeeForOffice, partnerSource),
    [employeeForOffice, partnerSource],
  );

  const foreignOfficePartnerId = foreignOfficePartner?._id
    ? String(foreignOfficePartner._id)
    : undefined;

  const showOfficePicker =
    user?.company?.type === ClientTypes.Moderator ||
    user?.company?.type === ClientTypes.LocalAgency;

  useEffect(() => {
    const next = resolveDefaultSelectOffice({
      employee: employeeForOffice,
      user: user ?? undefined,
      source: partnerSource,
    });
    if (next) {
      setCompany(next);
    }
  }, [employeeForOffice, partnerSource, user?.company]);

  const noEmbassyHint = useMemo(() => {
    const emp = selectedEmployee ?? currentEmployee;
    if (!emp || !isNoEmbassyCountry(emp.additionalData?.appliedCountry)) {
      return null;
    }
    if (emp.medicalStatus === MedicalStatus.fit) {
      return "No embassy step for this destination — visa will be marked issued on save.";
    }
    return "No embassy step for this destination — visa will be issued automatically once medical is FIT.";
  }, [selectedEmployee, currentEmployee]);

  return (
    <>
      <Box
        sx={{
          m: { xs: 0, md: 1 },
          px: { xs: 0, md: 2 },
        }}
      >
        <Stack spacing={1} sx={{ px: 2, pt: 2 }}>
          {user && user.company && showOfficePicker && (
            <CompanyFilter
              key={foreignOfficePartnerId ?? "visa-office"}
              companyType={ClientTypes.ForeignAgent}
              setCompany={setCompany}
              includeAll={false}
              companyId={foreignOfficePartnerId}
              value={company}
              offices={officeContext?.foreignOffices}
            />
          )}

          {foreignOfficePartner && !showOfficePicker && (
              <Typography variant="body2" color="text.secondary" sx={{ px: 0.5 }}>
                {t("calling.office")}:{" "}
                <strong>{foreignOfficePartner.displayName}</strong>
              </Typography>
            )}

          {employeeForOffice?._id && (
            <EmployeeRemarksButton employee={employeeForOffice} mode="bar" />
          )}

          <Formik
            enableReinitialize={true}
            initialValues={selectedEmployee as Employee}
            validationSchema={createVisaSchema}
            onSubmit={(values, actions) => {
              actions.setSubmitting(false);

              const { currentStatusDate: _omitStatusDate, ...employeeBase } =
                values;
              const office = applyOfficeSelectionToEmployee({
                employee: employeeBase,
                selectedOffice:
                  company?.type === ClientTypes.ForeignAgent
                    ? company
                    : undefined,
                source: partnerSource,
              });

              values = {
                ...employeeBase,
                _id: employeeBase._id ?? currentEmployee?._id,
                status: EmployeeStatus.contracted,
                partner: office.partner,
                owner: office.owner,
                parentCompany: office.parentCompany,
                additionalData: {
                  ...employeeBase.additionalData,
                  appliedCountry: office.appliedCountry,
                },
                notificationMessage:
                  NotificationMessageTypes.contracted as string,
              };
              dispatch(createEmployee(values));
            }}
          >
            {(props: FormikProps<Employee>) => (
              <Form>
                <Accordion expanded>
                  <StyledAccordionSummary
                    expandIcon={<ExpandMore />}
                    aria-controls="panel1a-content"
                    id="panel1a-header"
                  >
                    <Typography variant="h6" component="div">
                      {t("agreement") + " " + t("detail")}
                    </Typography>
                  </StyledAccordionSummary>
                  <AccordionDetails>
                    {noEmbassyHint && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                      >
                        {noEmbassyHint}
                      </Typography>
                    )}
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <FormikTextField
                          formikKey="givenName"
                          label={t("candidate") + " " + t("fullName")}
                          disabled
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FormikTextField
                          formikKey="visa.visaNumber"
                          label={t("visa_number")}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FormikTextField
                          formikKey="visa.passportNumber"
                          label={t("id_number")}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FormikTextField
                          formikKey="visa.fullName"
                          label={t("full_name")}
                        />
                      </Grid>
                      {/* <Grid item xs={12}>
                        <FormikTextField
                          formikKey="visa.sponsorBirthDate"
                          label={t("dateOfBirth")}
                        />
                      </Grid> */}
                    </Grid>
                    <Divider variant="middle" sx={{ m: 2 }} />

                    <Button
                      type="submit"
                      color="secondary"
                      fullWidth
                      variant="contained"
                      disabled={!props.isValid}
                    >
                      <Assignment />
                      {t("addAgreement")}
                    </Button>
                  </AccordionDetails>
                </Accordion>

                <br />
                {visaAdded && (
                  <Toast severity="success">{visaAdded.message}</Toast>
                )}
              </Form>
            )}
          </Formik>
          {visaFailed && <Toast severity="error">{visaFailed.message}</Toast>}
        </Stack>
      </Box>
    </>
  );
};
